import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useAppStore } from '@/store';
import MainLayout from '@/components/layout/MainLayout';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const UseAgent = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { addChat, addMessage, fetchAgentById, models } = useSupabaseStore();
  const { user } = useAppStore();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const createAgentChat = async () => {
      if (!agentId || !user || !models || models.length === 0) {
        // Wait for dependencies to be ready
        if (!user) navigate('/login');
        return;
      };

      try {
        setIsLoading(true);
        const agent = await fetchAgentById(agentId);
        if (!agent) {
          toast({ title: "Agent not found", variant: "destructive" });
          navigate('/agents/marketplace');
          return;
        }

        const defaultModel = models.find(m => m.tier === 'free') || models[0];
        if (!defaultModel) {
          toast({ title: "No available models", description: "Cannot create chat without an AI model.", variant: "destructive" });
          return;
        }

        const newChat = await addChat(
          user.id,
          `Chat with ${agent.name}`,
          defaultModel.id
        );

        if (newChat) {
          let systemPrompt = agent.instructions || `You are ${agent.name}.`;
          
          if (agent.blueprints && agent.blueprints.length > 0) {
            const blueprintInstructions = agent.blueprints
              .map(bp => bp.data?.nodes?.find(n => n.type === 'system-prompt')?.data?.content)
              .filter(Boolean)
              .join('\n\n');
            
            if (blueprintInstructions) {
              systemPrompt += `\n\n--- BLUEPRINT INSTRUCTIONS ---\n${blueprintInstructions}`;
            }
          }

          await addMessage(newChat.id, {
            content: systemPrompt,
            role: 'system',
            sender: 'system',
          });

          navigate(`/chat/${newChat.id}`);
        }
      } catch (error) {
        console.error("Failed to create agent chat", error);
        toast({ title: "Error", description: "Could not create a chat with this agent.", variant: "destructive" });
        navigate('/agents/marketplace');
      } finally {
        setIsLoading(false);
      }
    };

    createAgentChat();
  }, [agentId, models, user, addChat, addMessage, navigate, fetchAgentById]);

  return (
    <MainLayout>
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="mt-4 text-muted-foreground">Preparing your agent workspace...</p>
      </div>
    </MainLayout>
  );
};

export default UseAgent;